import { z } from "zod";
import type { Operation } from "../types";

/**
 * Activity rail (the app shell's right column). The raw `activities` table is
 * a flat firehose — dozens of near-identical "commit recorded" / "commit
 * linked to task" rows. This contract serves it ROLLED UP into groups keyed by
 * `<entity_kind>:<verb>`, each with a count + the most-recent event time + a
 * few members for expansion. Groups are dismissible (a "mark read up to here"
 * watermark, persisted per workspace in `activity_dismissals`).
 *
 * The `ActivityGroup`/`ActivityGroupMember` TYPES are the canonical ones from
 * `@excellent/core` (the single rollup engine, `groupActivities`); re-exported
 * here (type-only — erased at build) so the API contract and the engine can
 * never drift. The zod schemas below mirror that shape for runtime validation.
 */

export type { ActivityGroup, ActivityGroupMember } from "@excellent/core";

const memberSchema = z.object({
  id: z.number(),
  entity_id: z.number(),
  occurred_at: z.string(),
  href: z.string(),
  label: z.string().optional(),
});

const groupSchema = z.object({
  key: z.string(),
  label: z.string(),
  count: z.number(),
  latest: z.string(),
  href: z.string(),
  members: z.array(memberSchema),
});

const listQuery = z
  .object({
    env: z.enum(["demo", "real"]).optional(),
    category: z.string().optional(),
    q: z.string().optional(),
  })
  .strict();

const listResponse = z.object({
  groups: z.array(groupSchema),
  scope: z
    .object({
      environment: z.enum(["demo", "real"]),
      category: z.string().nullable(),
      q: z.string(),
      available: z.boolean(),
      unavailableReason: z.enum(["demo-not-recorded"]).nullable(),
    })
    .strict()
    .optional(),
});
type ListResponse = z.infer<typeof listResponse>;

const dismissBody = z.object({
  groupKey: z.string().min(1),
});
type DismissBody = z.infer<typeof dismissBody>;

const dismissResponse = z.object({ dismissed: z.literal(true) });
type DismissResponse = z.infer<typeof dismissResponse>;

const clearResponse = z.object({ cleared: z.number() });
type ClearResponse = z.infer<typeof clearResponse>;

const listOp: Operation<void, z.infer<typeof listQuery>, void, ListResponse> = {
  method: "GET",
  path: () => "/api/activity",
  query: listQuery,
  response: listResponse,
};

// Dismiss a single group: watermark it through "now" so its current events
// disappear while future events in the same group still surface.
const dismissOp: Operation<void, void, DismissBody, DismissResponse> = {
  method: "POST",
  path: () => "/api/activity",
  body: dismissBody,
  response: dismissResponse,
  // watermark removes the group from the rolled-up list.
  invalidates: [listOp],
};

// Clear all: watermark every currently-visible group through "now".
const clearOp: Operation<void, void, void, ClearResponse> = {
  method: "DELETE",
  path: () => "/api/activity",
  response: clearResponse,
  invalidates: [listOp],
};

export const activity = {
  list: listOp,
  dismiss: dismissOp,
  clear: clearOp,
};
